import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './Header.css';
import './Footer.css';
import './BulkSMSPage.css';
import './ContactUs.css';
import './BulkSMSLanding.css';

export const smsLandingSeo = {
  path: '/bulk-sms-kenya',
  url: 'https://peakmobile.co.ke/bulk-sms-kenya',
  title: 'Bulk SMS in Kenya | Business SMS Campaigns | Peak Mobile',
  description: 'Reach your customers with Peak Mobile bulk SMS in Kenya. Send personalised campaigns, reminders and alerts. Get a quote for your business today.',
};

export const smsLandingFaqs = [
  { question: 'What is bulk SMS, and how can my business use it?', answer: 'Bulk SMS lets you send a text message to many contacts in one campaign. Kenyan businesses can use it for promotions, appointment reminders, order updates, member notices and customer alerts. With Peak Mobile, you can manage campaigns through a web platform or connect messaging to your own software using an API.' },
  { question: 'Do my customers need an app or internet connection?', answer: 'No. SMS arrives in the phone’s regular messaging inbox, so recipients do not need to download an app or have mobile data. They do need an active mobile number, a compatible handset and mobile network coverage.' },
  { question: 'How much does bulk SMS cost?', answer: 'Your quote depends on your messaging volume and requirements. Tell us approximately how many messages you plan to send and our team will help you choose a suitable package. We will confirm pricing and any setup requirements with you before you get started.' },
  { question: 'Can I personalise and schedule my messages?', answer: 'Yes. Peak Mobile supports personalised messages, contact list management and scheduled SMS campaigns. You can plan an announcement in advance and tailor your message to the audience you want to reach.' },
  { question: 'Can I connect bulk SMS to my website or application?', answer: 'Yes. API access is available for developers who want to connect SMS to their own systems. Tell our team about your application and the messages you need to send so we can discuss the integration with you.' },
  { question: 'How do I get started with Peak Mobile?', answer: 'Request a bulk SMS quote using the form on this page. Share your business details and estimated monthly SMS volume, and our team will contact you to discuss your campaign and setup. Already have an account? You can sign in to the Peak Mobile platform below.' },
];

export const smsLandingSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization', '@id': 'https://peakmobile.co.ke/#organization',
      name: 'Peak Mobile', url: 'https://peakmobile.co.ke/',
      logo: 'https://peakmobile.co.ke/images/logo1.svg',
      telephone: '+254759422480', email: 'chat@peakmobile.co.ke',
      address: { '@type': 'PostalAddress', streetAddress: 'The Promenade, 3rd Floor, General Mathenge Drive', addressLocality: 'Nairobi', addressCountry: 'KE' },
    },
    {
      '@type': 'Service', '@id': `${smsLandingSeo.url}#service`,
      name: 'Bulk SMS in Kenya', serviceType: 'Business bulk SMS messaging',
      description: smsLandingSeo.description, url: smsLandingSeo.url,
      areaServed: { '@type': 'Country', name: 'Kenya' },
      provider: { '@id': 'https://peakmobile.co.ke/#organization' },
    },
    {
      '@type': 'FAQPage', '@id': `${smsLandingSeo.url}#faqs`,
      mainEntity: smsLandingFaqs.map(({ question, answer }) => ({ '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer } })),
    },
  ],
};

export function trackSmsAction(action, placement) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', action, { event_category: 'bulk_sms_landing', placement });
  }
}

function QuoteLink({ children = 'Get my bulk SMS quote', placement, className = '' }) {
  return <a className={`btn-primary-sms smslp-button ${className}`} href="#sms-quote" onClick={() => trackSmsAction('sms_quote_click', placement)}>{children}<span aria-hidden="true">→</span></a>;
}

const whatsappUrl = `https://wa.me/254759422480?text=${encodeURIComponent('Hi Peak Mobile, I’d like to learn more about your customer engagement solutions.')}`;

const solutions = [
  { title: 'Bulk SMS', icon: 'sms.svg', description: 'Send branded messages to thousands of customers in seconds.', href: '/bulk-sms' },
  { title: 'Bulk Airtime', icon: 'airtimenav.svg', description: 'Reward customers, staff and agents with instant airtime top-ups.', href: '/airtime-rewards' },
  { title: 'Bulk Data', icon: 'data.svg', description: 'Say thank you with mobile data your customers will actually use.', href: '/bulk-data' },
  { title: 'WhatsApp Messaging', icon: 'whatsapp.svg', description: 'Reach customers on the app they open every day, with rich, two-way conversations.', href: '/bulk-whatsapp' },
];

const campaignSteps = [
  { title: 'Share your goal', description: 'Tell us who you want to reach and what you want them to do.' },
  { title: 'Set up your campaign', description: 'Choose your channels and rewards. We’ll help you get ready to launch.' },
  { title: 'Engage and grow', description: 'Reach your customers and follow your campaign’s progress from your dashboard.' },
];

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.5 11.7a8.5 8.5 0 0 1-12.7 7.4L3 20.5l1.4-4.7a8.5 8.5 0 1 1 16.1-4.1Z" />
      <path d="m8.4 7.6 1.3-.1 1.1 2.4-.9 1.1a8.1 8.1 0 0 0 2.9 2.8l1.1-.9 2.3 1.1-.1 1.3c-.1.8-.8 1.3-1.6 1.2-4.2-.5-7.5-3.8-7.8-7.6 0-.7.6-1.3 1.7-1.3Z" />
    </svg>
  );
}

function WhatsAppLink({ children = 'Chat on WhatsApp', placement, className = '' }) {
  return (
    <a className={`smslp-button smslp-button--whatsapp ${className}`} href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackSmsAction('sms_whatsapp_click', placement)}>
      <WhatsAppIcon />{children}
    </a>
  );
}

function LandingMetadata() {
  useEffect(() => {
    document.body.classList.add('sms-landing-active');
    document.title = smsLandingSeo.title;
    const changes = [];
    const setTag = (selector, tagName, attributes) => {
      let element = document.head.querySelector(selector);
      const existed = Boolean(element && !element.hasAttribute('data-sms-landing'));
      const previous = element ? element.outerHTML : null;
      if (!element) {
        element = document.createElement(tagName);
        document.head.appendChild(element);
      }
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      changes.push(() => {
        if (existed) element.outerHTML = previous;
        else element.remove();
      });
    };
    const description = document.head.querySelector('meta[name="description"]');
    if (description) description.content = smsLandingSeo.description;
    else setTag('meta[name="description"]', 'meta', { name: 'description', content: smsLandingSeo.description });
    setTag('link[rel="canonical"]', 'link', { rel: 'canonical', href: smsLandingSeo.url, 'data-sms-landing': 'true' });
    const meta = {
      'og:title': smsLandingSeo.title, 'og:description': smsLandingSeo.description,
      'og:url': smsLandingSeo.url, 'og:type': 'website', 'og:site_name': 'Peak Mobile',
      'og:locale': 'en_KE', 'og:image': 'https://peakmobile.co.ke/images/sms-service.jpg',
      'og:image:alt': 'Peak Mobile business messaging',
    };
    Object.entries(meta).forEach(([property, content]) => setTag(`meta[property="${property}"]`, 'meta', { property, content, 'data-sms-landing': 'true' }));
    Object.entries({ 'twitter:card': 'summary_large_image', 'twitter:title': smsLandingSeo.title, 'twitter:description': smsLandingSeo.description, 'twitter:image': meta['og:image'] }).forEach(([name, content]) => setTag(`meta[name="${name}"]`, 'meta', { name, content, 'data-sms-landing': 'true' }));
    let schema = document.getElementById('sms-landing-schema');
    if (!schema) {
      schema = document.createElement('script');
      schema.id = 'sms-landing-schema';
      schema.type = 'application/ld+json';
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify(smsLandingSchema);
    return () => {
      document.body.classList.remove('sms-landing-active');
      document.title = 'Peak Mobile';
      const descriptionTag = document.head.querySelector('meta[name="description"]');
      if (descriptionTag) descriptionTag.content = 'Peak Mobile Website';
      changes.reverse().forEach(restore => restore());
      schema.remove();
    };
  }, []);
  return null;
}

function QuoteForm() {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const successRef = useRef(null);
  const configured = Boolean(process.env.REACT_APP_EMAILJS_SERVICE_ID && process.env.REACT_APP_EMAILJS_TEMPLATE_ID && process.env.REACT_APP_EMAILJS_PUBLIC_KEY && process.env.REACT_APP_RECAPTCHA_SITE_KEY);

  useEffect(() => {
    if (status === 'success') successRef.current?.focus();
  }, [status]);

  async function submitQuote(event) {
    event.preventDefault();
    if (status === 'sending') return;
    if (!captchaToken) {
      setError('Please complete the verification below before sending your request.');
      return;
    }
    const form = event.currentTarget;
    const values = new FormData(form);
    const fields = ['name', 'email', 'phone', 'company', 'volume'];
    if (fields.some(field => !String(values.get(field) || '').trim())) {
      setError('Please complete all the fields so we can prepare your quote.');
      return;
    }
    setStatus('sending');
    setError('');
    const params = new URLSearchParams(window.location.search);
    const attribution = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'msclkid']
      .filter(key => params.has(key))
      .map(key => `${key}: ${params.get(key).slice(0, 300)}`).join('\n');
    try {
      const emailjs = await import('@emailjs/browser');
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: values.get('name').trim(), from_email: values.get('email').trim(),
          phoneNumber: values.get('phone').trim(), company_name: values.get('company').trim(),
          industry: 'Bulk SMS enquiry',
          message: `BULK SMS QUOTE REQUEST\nEstimated monthly SMS volume: ${values.get('volume')}\nLanding page: ${smsLandingSeo.url}${attribution ? `\nCampaign details:\n${attribution}` : ''}`,
          to_email: 'tedaringo628@gmail.com',
          'g-recaptcha-response': captchaToken,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setStatus('success');
      trackSmsAction('generate_lead', 'quote_form');
    } catch {
      setStatus('idle');
      setError('Your request could not be sent. Please try again, or call us on +254 759 422 480.');
    } finally {
      captchaRef.current?.reset();
      setCaptchaToken('');
    }
  }

  if (status === 'success') return (
    <div className="contact-form-card smslp-success" ref={successRef} tabIndex="-1" role="status">
      <p className="form-success">Your bulk SMS quote request has been sent. Our team will contact you using the details you shared.</p>
      <p>You can also <a href="tel:+254759422480">call +254 759 422 480</a>.</p>
    </div>
  );

  if (!configured) return (
    <div className="contact-form-card smslp-form-fallback">
      <p>Call or email our team with your business name and estimated SMS volume to request a quote.</p>
      <a className="btn-primary-sms smslp-button" href="tel:+254759422480">Call +254 759 422 480</a>
      <a href="mailto:chat@peakmobile.co.ke?subject=Bulk%20SMS%20quote%20request">Email chat@peakmobile.co.ke</a>
    </div>
  );

  return (
    <form className="contact-form-card smslp-form" onSubmit={submitQuote} method="post" aria-label="Request a bulk SMS quote" aria-busy={status === 'sending'}>
      <fieldset disabled={status === 'sending'}>
        <div className="smslp-form-grid">
          <label className="form-group" htmlFor="sms-name">Your name<input id="sms-name" name="name" autoComplete="name" placeholder="Full name" maxLength="120" required /></label>
          <label className="form-group" htmlFor="sms-company">Business name<input id="sms-company" name="company" autoComplete="organization" placeholder="Your company" maxLength="160" required /></label>
          <label className="form-group" htmlFor="sms-email">Work email<input id="sms-email" name="email" type="email" autoComplete="email" placeholder="you@company.com" maxLength="254" required /></label>
          <label className="form-group" htmlFor="sms-phone">Phone number<input id="sms-phone" name="phone" type="tel" autoComplete="tel" placeholder="e.g. 0712 345 678" maxLength="30" pattern={'[+0-9\\(\\) .\\-]{7,30}'} required /></label>
        </div>
        <label className="form-group" htmlFor="sms-volume">Estimated SMS per month<select id="sms-volume" name="volume" defaultValue="" required>
          <option value="" disabled>Select your monthly volume</option>
          <option>Under 25,000</option><option>25,000–125,000</option><option>125,001–524,999</option><option>525,000 or more</option><option>Help me decide</option>
        </select></label>
        <label className="smslp-consent"><input type="checkbox" required name="consent" /><span>I agree to be contacted about my bulk SMS enquiry. See our <a href="/privacy">privacy policy</a>.</span></label>
        <div className="smslp-captcha"><ReCAPTCHA ref={captchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={token => { setCaptchaToken(token || ''); setError(''); }} onExpired={() => setCaptchaToken('')} onErrored={() => { setCaptchaToken(''); setError('Verification could not load. Please try again, or call +254 759 422 480.'); }} /></div>
        {error && <p className="form-error smslp-error" role="alert">{error}</p>}
        <button type="submit" className="btn-primary-sms smslp-button">{status === 'sending' ? 'Sending your request…' : 'Get my bulk SMS quote'}<span aria-hidden="true">→</span></button>
      </fieldset>
    </form>
  );
}

export default function BulkSMSLanding() {
  return (
    <div className="sms-landing bulk-sms-page">
      <LandingMetadata />
      <a className="smslp-skip" href="#sms-main">Skip to content</a>
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <a href="/" aria-label="Peak Mobile homepage"><img src="/images/logo.jpg" alt="Peak Mobile" width="128" height="68" /></a>
          </div>
          <nav className="smslp-navigation" aria-label="Bulk SMS page">
            <a href="#sms-solutions">Solutions</a>
            <a href="#sms-how-it-works">How it works</a>
            <a href="#sms-quote">Contact</a>
            <a href="https://app.peakmobile.co.ke/signin" onClick={() => trackSmsAction('sms_signin_click', 'header')}>Log in</a>
          </nav>
          <WhatsAppLink placement="header" className="smslp-button--orange" />
        </div>
      </header>

      <main id="sms-main">
        <section className="smslp-hero" aria-labelledby="sms-hero-heading">
          <div className="smslp-container smslp-hero-grid">
            <div className="smslp-hero-copy">
              <p className="smslp-tagline">Connect. Engage. Grow</p>
              <h1 id="sms-hero-heading">Reach, reward <br />and retain <span>every customer.</span></h1>
              <p className="smslp-hero-description">Bulk SMS in Kenya, plus airtime, data and WhatsApp messaging in one platform. Reach your customers and keep them coming back.</p>
              <div className="smslp-actions">
                <WhatsAppLink placement="hero">Talk to us on WhatsApp</WhatsAppLink>
                <QuoteLink placement="hero" className="smslp-button--outline">Get a bulk SMS quote</QuoteLink>
              </div>
            </div>
            <div className="smslp-message-preview" aria-hidden="true">
              <div className="smslp-sms-message">
                <span>BULK SMS</span>
                <p>Great offers just for you! Buy now while stocks last.</p>
              </div>
              <div className="smslp-notification">
                <img src="/images/airtimenav.svg" alt="" width="42" height="42" />
                <p>Airtime reward sent to your number</p>
              </div>
              <div className="smslp-notification">
                <img src="/images/data.svg" alt="" width="42" height="42" />
                <p>Thank you! Enjoy free data on us</p>
              </div>
              <div className="smslp-notification">
                <img src="/images/whatsapp.svg" alt="" width="42" height="42" />
                <p>New WhatsApp message from your favourite store</p>
              </div>
            </div>
          </div>
        </section>

        <section id="sms-solutions" className="smslp-solutions smslp-container" aria-labelledby="sms-solutions-title">
          <p className="smslp-eyebrow">Our solutions</p>
          <h2 id="sms-solutions-title">Everything you need to <br />engage customers</h2>
          <p className="smslp-section-description">Use one solution or combine them into a single campaign.</p>
          <div className="smslp-solution-grid">
            {solutions.map(({ title, icon, description, href }) => (
              <a className="smslp-solution-card" href={href} key={title}>
                <img src={`/images/${icon}`} alt="" width="46" height="46" loading="lazy" />
                <h3>{title}</h3>
                <p>{description}</p>
                <span className="smslp-card-link">Explore solution <span aria-hidden="true">→</span></span>
              </a>
            ))}
          </div>
          <div className="smslp-help-banner">
            <div>
              <h3>Not sure which fits your business?</h3>
              <p>Tell us your goal and we’ll recommend the right mix.</p>
            </div>
            <WhatsAppLink placement="solutions" className="smslp-button--orange">Ask us on WhatsApp</WhatsAppLink>
          </div>
        </section>

        <section id="sms-how-it-works" className="smslp-how" aria-labelledby="sms-how-title">
          <div className="smslp-container">
            <p className="smslp-eyebrow">How it works</p>
            <h2 id="sms-how-title">Up and running in three steps</h2>
            <ol className="smslp-step-grid">
              {campaignSteps.map(({ title, description }, index) => (
                <li className="smslp-step" key={title}>
                  <span className="smslp-step-number" aria-hidden="true">0{index + 1}</span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="sms-quote" className="smslp-quote-section smslp-container" aria-labelledby="sms-quote-title">
          <div className="smslp-quote-grid">
            <div className="smslp-quote-copy">
              <p className="smslp-eyebrow">Let’s get started</p>
              <h2 id="sms-quote-title">Grow your business <br />with Bulk SMS</h2>
              <p>Share your business details and estimated monthly SMS volume. We’ll help you choose the right package and get your campaign moving.</p>
              <p className="smslp-contact-label">Prefer a conversation? Get in touch.</p>
              <p><a href="tel:+254759422480" onClick={() => trackSmsAction('sms_call_click', 'quote_section')}>+254 759 422 480</a><br /><a href="mailto:chat@peakmobile.co.ke">chat@peakmobile.co.ke</a></p>
              <p className="smslp-hours">Monday–Friday, 8:00 AM–5:00 PM EAT</p>
            </div>
            <div className="smslp-quote-form">
              <noscript><div className="contact-form-card smslp-form-fallback"><p>For a bulk SMS quote, call <a href="tel:+254759422480">+254 759 422 480</a> or email <a href="mailto:chat@peakmobile.co.ke?subject=Bulk%20SMS%20quote%20request">chat@peakmobile.co.ke</a>.</p></div></noscript>
              <QuoteForm />
            </div>
          </div>
        </section>

        <section id="sms-faqs" className="smslp-faqs smslp-container" aria-labelledby="sms-faq-title">
          <p className="smslp-eyebrow">A little more detail</p>
          <h2 id="sms-faq-title">Your questions, answered</h2>
          {smsLandingFaqs.map(({ question, answer }) => (
            <details key={question}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
          <p className="smslp-signin"><a href="https://app.peakmobile.co.ke/signin" onClick={() => trackSmsAction('sms_signin_click', 'existing_customer')}>Sign in to send bulk SMS <span aria-hidden="true">→</span></a></p>
        </section>

        <section className="smslp-final-cta smslp-container" aria-labelledby="sms-contact-title">
          <h2 id="sms-contact-title">Ready to amplify your rewards?</h2>
          <p>Chat with our team today and get a solution tailored to your business.</p>
          <div className="smslp-actions">
            <WhatsAppLink placement="final_cta" />
            <a className="smslp-button smslp-button--light-outline" href="tel:+254759422480" onClick={() => trackSmsAction('sms_call_click', 'final_cta')}>Call +254 759 422 480</a>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-bottom">
          <div className="footer-container">
            <p className="copyright">Peak Mobile · Connect. Engage. Grow</p>
            <div className="legal-links"><a href="/privacy">Privacy policy</a><a href="https://app.peakmobile.co.ke/signin">app.peakmobile.co.ke</a><a href="tel:+254759422480">+254 759 422 480</a></div>
          </div>
        </div>
      </footer>
    </div>
  );
}
